import React, { useState, useEffect } from 'react';
import { Card, Button, Table, Modal, Form, Alert, Badge, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faTrash, faKey, faClipboard, faCheck, faToggleOn, faToggleOff, faInfoCircle, faSearch } from '@fortawesome/free-solid-svg-icons';
import axios from 'axios';
import { API_URL } from '../config';

function ApiKeys() {
  const [apiKeys, setApiKeys] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [currentKey, setCurrentKey] = useState(null);
  const [keyName, setKeyName] = useState('');
  const [copiedKey, setCopiedKey] = useState('');
  const [showDocsModal, setShowDocsModal] = useState(false);

  useEffect(() => {
    fetchApiKeys();
  }, []);

  const fetchApiKeys = async () => {
    try {
      setIsLoading(true);
      const response = await axios.get(`${API_URL}/api-keys`);
      
      // Log the response structure to debug
      console.log('API Keys response:', response.data);
      
      // Handle various response structures to ensure we always have an array
      let keysArray = [];
      if (response.data) {
        if (Array.isArray(response.data)) {
          keysArray = response.data;
        } else if (response.data.apiKeys) {
          if (Array.isArray(response.data.apiKeys)) {
            keysArray = response.data.apiKeys;
          } else if (typeof response.data.apiKeys === 'object') {
            keysArray = Object.values(response.data.apiKeys);
          }
        } else if (typeof response.data === 'object' && !Array.isArray(response.data)) {
          // If response.data is a single object, wrap it in an array
          keysArray = [response.data];
        }
      }
      
      setApiKeys(keysArray);
      setError('');
    } catch (err) {
      console.error('Error fetching API keys:', err);
      setError('Failed to load API keys');
      setApiKeys([]); // Ensure it's an empty array on error
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateKey = async () => {
    if (!keyName.trim()) {
      setError('API key name is required');
      return;
    }

    try {
      setError('');
      setSuccess('');
      
      const response = await axios.post(`${API_URL}/api-keys`, { name: keyName });
      
      setApiKeys([response.data.apiKey, ...apiKeys]);
      setKeyName('');
      setCurrentKey(response.data.apiKey);
      setSuccess('API key created successfully');
      setShowCreateModal(false);
      
      // Open the success modal with the new key
      setCopiedKey(response.data.apiKey.api_key);
      setTimeout(() => setCopiedKey(''), 30000); // Clear copied key after 30 seconds
    } catch (err) {
      console.error('Error creating API key:', err);
      setError(err.response?.data?.message || 'Failed to create API key');
    }
  };

  const handleDeleteKey = async () => {
    try {
      setError('');
      setSuccess('');
      
      await axios.delete(`${API_URL}/api-keys/${currentKey.id}`);
      
      setApiKeys(apiKeys.filter(key => key.id !== currentKey.id));
      setSuccess('API key deleted successfully');
      setShowDeleteModal(false);
    } catch (err) {
      console.error('Error deleting API key:', err);
      setError(err.response?.data?.message || 'Failed to delete API key');
    }
  };

  const toggleKeyStatus = async (keyId, currentStatus) => {
    try {
      setError('');
      setSuccess('');
      
      await axios.put(`${API_URL}/api-keys/${keyId}/status`, { 
        active: !currentStatus 
      });
      
      setApiKeys(apiKeys.map(key => {
        if (key.id === keyId) {
          return { ...key, active: !key.active };
        }
        return key;
      }));
      
      setSuccess(`API key ${!currentStatus ? 'activated' : 'deactivated'} successfully`);
    } catch (err) {
      console.error('Error updating API key status:', err);
      setError(err.response?.data?.message || 'Failed to update API key status');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(
      () => {
        setCopiedKey(text);
        setTimeout(() => setCopiedKey(''), 3000); // Clear after 3 seconds
      },
      (err) => console.error('Could not copy text: ', err)
    );
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  if (isLoading) {
    return (
      <div className="text-center p-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <p className="mt-3">Loading API keys...</p>
      </div>
    );
  }

  return (
    <>
      <Card className="shadow-sm">
        <Card.Header className="d-flex justify-content-between align-items-center">
          <h5 className="m-0">
            <FontAwesomeIcon icon={faKey} className="me-2" /> API Keys
          </h5>
          <div>
            <Button 
              variant="outline-info" 
              size="sm" 
              className="me-2"
              onClick={() => setShowDocsModal(true)}
            >
              <FontAwesomeIcon icon={faInfoCircle} className="me-1" /> API Documentation
            </Button>
            <Button 
              variant="primary" 
              size="sm"
              onClick={() => setShowCreateModal(true)}
            >
              <FontAwesomeIcon icon={faPlus} className="me-1" /> Create API Key
            </Button>
          </div>
        </Card.Header>
        <Card.Body>
          {error && <Alert variant="danger">{error}</Alert>}
          {success && <Alert variant="success">{success}</Alert>}
          
          {/* Add type check before using .map() */}
          {!Array.isArray(apiKeys) ? (
            <Alert variant="danger">
              Error: API keys data is not in the expected format. Please refresh the page or contact support.
            </Alert>
          ) : apiKeys.length === 0 ? (
            <div className="text-center py-5">
              <FontAwesomeIcon icon={faKey} size="3x" className="text-muted mb-3" />
              <h5>No API Keys Found</h5>
              <p className="text-muted">Create an API key to access our external APIs.</p>
              <Button variant="primary" onClick={() => setShowCreateModal(true)}>
                <FontAwesomeIcon icon={faPlus} className="me-1" /> Create API Key
              </Button>
            </div>
          ) : (
            <Table responsive hover>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>API Key</th>
                  <th>Created</th>
                  <th>Last Used</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {apiKeys.filter(key => key !== null).map((key) => (
                  <tr key={key?.id || (key?.api_key && key.api_key.substring(0, 8)) || Math.random().toString()}>
                    <td>{key?.name || 'Unnamed Key'}</td>
                    <td>
                      {key?.api_key ? (
                        <div className="d-flex align-items-center">
                          <code className="me-2">
                            {key.api_key.substring(0, 12)}...
                          </code>
                          <OverlayTrigger
                            placement="top"
                            overlay={
                              <Tooltip>
                                {copiedKey === key.api_key ? "Copied!" : "Copy to clipboard"}
                              </Tooltip>
                            }
                          >
                            <Button 
                              variant="outline-secondary" 
                              size="sm"
                              onClick={() => copyToClipboard(key.api_key)}
                            >
                              <FontAwesomeIcon 
                                icon={copiedKey === key.api_key ? faCheck : faClipboard} 
                              />
                            </Button>
                          </OverlayTrigger>
                        </div>
                      ) : (
                        <span className="text-muted">No API key available</span>
                      )}
                    </td>
                    <td>{key?.created_at ? formatDate(key.created_at) : 'N/A'}</td>
                    <td>{key?.last_used_at ? formatDate(key.last_used_at) : 'Never'}</td>
                    <td>
                      <Badge 
                        bg={key?.active ? "success" : "danger"}
                        style={{ cursor: 'pointer' }}
                        onClick={() => key?.id && toggleKeyStatus(key.id, key.active)}
                      >
                        <FontAwesomeIcon 
                          icon={key?.active ? faToggleOn : faToggleOff} 
                          className="me-1" 
                        />
                        {key?.active ? "Active" : "Inactive"}
                      </Badge>
                    </td>
                    <td>
                      <Button 
                        variant="danger" 
                        size="sm"
                        onClick={() => {
                          setCurrentKey(key);
                          setShowDeleteModal(true);
                        }}
                        disabled={!key?.id}
                      >
                        <FontAwesomeIcon icon={faTrash} />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      {/* Create API Key Modal */}
      <Modal show={showCreateModal} onHide={() => setShowCreateModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Create API Key</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>API Key Name</Form.Label>
              <Form.Control
                type="text"
                value={keyName}
                onChange={(e) => setKeyName(e.target.value)}
                placeholder="Enter a descriptive name"
                required
              />
              <Form.Text className="text-muted">
                Give your API key a descriptive name for your reference
              </Form.Text>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowCreateModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleCreateKey}>
            Create API Key
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Delete API Key Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Delete API Key</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {currentKey && (
            <p>
              Are you sure you want to delete the API key "{currentKey.name || 'Unnamed Key'}"?
              This action cannot be undone.
            </p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button 
            variant="danger" 
            onClick={handleDeleteKey}
            disabled={!currentKey || !currentKey.id}
          >
            Delete
          </Button>
        </Modal.Footer>
      </Modal>

      {/* New Key Created Success Modal */}
      <Modal show={!!copiedKey} onHide={() => setCopiedKey('')}>
        <Modal.Header closeButton>
          <Modal.Title>API Key Created</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="warning">
            <FontAwesomeIcon icon={faInfoCircle} className="me-2" />
            This is the only time you'll see the full API key. Please copy it now!
          </Alert>
          <div className="d-flex align-items-center mb-3">
            <code className="p-2 bg-light w-100 me-2 border rounded">{copiedKey}</code>
            <Button 
              variant="outline-primary" 
              onClick={() => copyToClipboard(copiedKey)}
            >
              <FontAwesomeIcon icon={faClipboard} />
            </Button>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="success" onClick={() => setCopiedKey('')}>
            I've Saved My API Key
          </Button>
        </Modal.Footer>
      </Modal>

      {/* API Documentation Modal */}
      <Modal show={showDocsModal} onHide={() => setShowDocsModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>API Documentation</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <h5>Authentication</h5>
          <p>
            All API requests require the <code>X-API-Key</code> header with your API key.
          </p>
          <pre className="bg-light p-3 rounded">
            X-API-Key: AI_your_api_key_here
          </pre>

          <hr/>

          <h5>Process a Message with AI</h5>
          <p>Send a message to be processed by AI and get the response.</p>
          <pre className="bg-light p-3 rounded">
{`POST /api/external/process
Content-Type: application/json

{
  "message": "Tell me about product XYZ",
  "conversation_id": "optional_previous_conversation_id"
}`}
          </pre>

          <h5>Send WhatsApp Message</h5>
          <p>Send a message via WhatsApp using your configured WhatsApp account.</p>
          <pre className="bg-light p-3 rounded">
{`POST /api/external/whatsapp
Content-Type: application/json

{
  "recipient": "1234567890",
  "message": "Your order has been confirmed!"
}`}
          </pre>

          <h5>Send Telegram Message</h5>
          <p>Send a message via Telegram using your configured Telegram bot.</p>
          <pre className="bg-light p-3 rounded">
{`POST /api/external/telegram
Content-Type: application/json

{
  "chat_id": "12345678",
  "message": "Your order has been confirmed!"
}`}
          </pre>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={() => setShowDocsModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}

export default ApiKeys;
